/**
 * Location for useChatApp: the near-me permission prompt that gates a turn,
 * Share location, and their watchdogs. Split out of useChatApp.js unchanged
 * (2026-09-25 gap closure).
 */
import { useEffect, useCallback } from 'react';
import { nextMessageId } from '../../utils/messageIds';
import {
  LOCATION_DENIED_MSG,
  LOCATION_LATE_MSG,
  LOCATION_TIMEOUT_MSG,
  LOCATION_WATCHDOG_MS,
  boundedReverseGeocode,
} from './chatConstants';

/**
 * @param {object} s - Context from useChatState.
 * @param {{ authToken: string|null, requiresGuardianAuth: boolean, openAuthModal: Function }} opts
 */
export function useChatLocation(s, { authToken, requiresGuardianAuth, openAuthModal }) {
  const {
    setMessages, setInputMessage, setIsLoading, setIsLocating, inFlightRef, turnEpochRef,
    awaitingLocationRef, chatEpochRef, locationWatchdogRef, shareWatchdogRef, locationSeqRef,
    endTurn, setClientLocation, setLocationError,
  } = s;

  // Unmount only: a pending Share-location watchdog must not fire afterwards.
  useEffect(() => () => clearTimeout(shareWatchdogRef.current), [shareWatchdogRef]);

  /**
   * Near-me turn without a saved location: adds the user bubble, holds the
   * turn while the browser permission prompt is open, then sends through
   * `dispatchNearMeMessage` (from the same render as the calling sendMessage).
   * @param {string} text
   * @param {(text: string, loc: object, preAddedId: number) => string} dispatchNearMeMessage
   * @returns {'sent'|'no-geolocation'}
   */
  const promptNearMeLocation = (text, dispatchNearMeMessage) => {
    const userMessage = {
      id: nextMessageId(),
      type: 'user',
      content: text,
      timestamp: new Date().toLocaleTimeString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');

    if (!navigator.geolocation) {
      endTurn();
      setMessages((prev) => [
        ...prev,
        {
          id: nextMessageId(),
          type: 'assistant',
          content:
            'This browser does not support location sharing. Name a place instead '
            + '(for example, “fires near Malibu”), or try another browser.',
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
      return 'no-geolocation';
    }

    // The turn is in flight from here: a second send or Confirm during the
    // location prompt must be refused like any other concurrent send.
    inFlightRef.current = true;
    awaitingLocationRef.current = true;
    setIsLoading(true);
    const epoch = turnEpochRef.current;
    const chatEpoch = chatEpochRef.current;
    locationSeqRef.current += 1;
    const seq = locationSeqRef.current;
    clearTimeout(shareWatchdogRef.current); // this prompt supersedes it
    setIsLocating(true);
    setLocationError(null);
    // The geolocation timeout only starts after permission is granted, so a
    // prompt that is ignored or dismissed without a callback would hold the
    // turn forever (2026-09-25 pass 4 C1).
    clearTimeout(locationWatchdogRef.current);
    locationWatchdogRef.current = setTimeout(() => {
      if (turnEpochRef.current !== epoch) return;
      endTurn();
      setMessages((prev) => [
        ...prev,
        {
          id: nextMessageId(),
          type: 'assistant',
          content: LOCATION_TIMEOUT_MSG,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
    }, LOCATION_WATCHDOG_MS);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        // The browser answered in time: the turn owns itself from here, so
        // the watchdog cannot cut in during the geocode wait (pass 7).
        // The epoch check after the await still catches a retire, chat
        // switch or sign-out during it.
        if (turnEpochRef.current === epoch) {
          clearTimeout(locationWatchdogRef.current);
        }
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        const accuracy_m = pos.coords.accuracy;
        let placeName = null;
        try {
          placeName = await boundedReverseGeocode(authToken, lat, lon);
        } catch {
          placeName = null;
        }
        const loc = { lat, lon, accuracy_m, placeName };
        if (turnEpochRef.current !== epoch) {
          // Socket retired, watchdog fired, or chat switched while the
          // prompt was open. Keep the location for the same chat and say
          // so, rather than leaving the question silently unanswered --
          // unless a newer prompt is already answering it.
          // Only the latest prompt for this chat may touch location state
          // (pass 5 F4; clientLocation too, pass 8).
          const ownsLatest = chatEpochRef.current === chatEpoch
            && locationSeqRef.current === seq;
          if (ownsLatest) {
            setClientLocation(loc);
            setMessages((prev) => [
              ...prev,
              {
                id: nextMessageId(),
                type: 'assistant',
                content: LOCATION_LATE_MSG,
                timestamp: new Date().toLocaleTimeString(),
              },
            ]);
          }
          return;
        }
        clearTimeout(locationWatchdogRef.current);
        setIsLocating(false);
        setLocationError(null); // e.g. a refused Share location (pass 6 L2)
        awaitingLocationRef.current = false;
        setClientLocation(loc);
        dispatchNearMeMessage(text, loc, userMessage.id);
      },
      (err) => {
        if (turnEpochRef.current !== epoch) return;
        endTurn();
        const denied = err?.code === 1;
        setLocationError(
          denied
            ? 'Location permission denied.'
            : 'Could not get your location. Try again or type a place name.',
        );
        setMessages((prev) => [
          ...prev,
          {
            id: nextMessageId(),
            type: 'assistant',
            content: denied
              ? LOCATION_DENIED_MSG
              : 'Could not get your location. Try Share location from the + menu, or name a place.',
            timestamp: new Date().toLocaleTimeString(),
          },
        ]);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
    );
    return 'sent';
  };

  /** Request browser geolocation (permission popup) for near-me queries (~50 km). */
  const shareLocation = useCallback(() => {
    // Only a near-me turn waiting on its own prompt blocks this; an ordinary
    // turn does not (2026-09-25 pass 5 F1). Never refuse silently.
    if (awaitingLocationRef.current) {
      setLocationError('Already waiting for your location.');
      return;
    }
    if (requiresGuardianAuth) {
      openAuthModal();
      return;
    }
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported in this browser.');
      setMessages((prev) => [
        ...prev,
        {
          id: nextMessageId(),
          type: 'assistant',
          content:
            'This browser does not support location sharing. Name a place instead, or try another browser.',
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
      return;
    }
    // The result belongs to the chat that asked, and only while no newer
    // prompt has started (2026-09-25 pass 5 F2/F4).
    locationSeqRef.current += 1;
    const seq = locationSeqRef.current;
    const chatEpoch = chatEpochRef.current;
    const isLatest = () => locationSeqRef.current === seq;
    const settle = () => {
      if (!isLatest()) return false;
      clearTimeout(shareWatchdogRef.current);
      setIsLocating(false);
      return chatEpochRef.current === chatEpoch;
    };
    setIsLocating(true);
    setLocationError(null);
    // An ignored prompt fires neither callback (2026-09-25 pass 5 F3).
    clearTimeout(shareWatchdogRef.current);
    shareWatchdogRef.current = setTimeout(() => {
      if (!isLatest()) return;
      locationSeqRef.current += 1; // a late answer is now stale
      setIsLocating(false);
      if (chatEpochRef.current === chatEpoch) {
        setLocationError(LOCATION_TIMEOUT_MSG);
      }
    }, LOCATION_WATCHDOG_MS);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        // The browser answered: this request now owns itself, so the
        // geocode wait cannot be pre-empted by the watchdog (pass 6 L1).
        if (!isLatest()) return;
        clearTimeout(shareWatchdogRef.current);
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        const accuracy_m = pos.coords.accuracy;
        let placeName = null;
        try {
          placeName = await boundedReverseGeocode(authToken, lat, lon);
        } catch {
          placeName = null;
        }
        if (!settle()) return;
        setClientLocation({ lat, lon, accuracy_m, placeName });
        setMessages((prev) => [
          ...prev,
          {
            id: nextMessageId(),
            type: 'assistant',
            content: placeName
              ? `Location shared: ${placeName}. I will search about a 50 km radius for near-me questions.`
              : `Location shared (${lat.toFixed(5)}, ${lon.toFixed(5)}). I will search about a 50 km radius for near-me questions.`,
            timestamp: new Date().toLocaleTimeString(),
          },
        ]);
      },
      (err) => {
        if (!settle()) return;
        const denied = err?.code === 1;
        setLocationError(
          denied
            ? 'Location permission denied.'
            : 'Could not get your location. Try again or type a place name.',
        );
        setMessages((prev) => [
          ...prev,
          {
            id: nextMessageId(),
            type: 'assistant',
            content: denied
              ? LOCATION_DENIED_MSG
              : 'Could not get your location. Try again, or name a place.',
            timestamp: new Date().toLocaleTimeString(),
          },
        ]);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
    );
  }, [authToken, awaitingLocationRef, chatEpochRef, locationSeqRef, openAuthModal, requiresGuardianAuth, setClientLocation, setIsLocating, setLocationError, setMessages, shareWatchdogRef]);

  return { promptNearMeLocation, shareLocation };
}
